export const FEEDBACK_COLLECTOR_ENTRYPOINT_ID = '45c9c029-3180-4960-ab0d-db7cdeaf5f54';
export const MS_IN_SECOND = 1000;
export const SECONDS_IN_MIN = 60;
export const SECONDS_IN_HR: number = SECONDS_IN_MIN * 60;
export const LS_PROVIDER_KEY = 'aiMateSmartLinkProviders';

/**
 * Base localStorage key for the unified auto-load conversation state.
 * Stores a JSON-encoded object with the selected conversationId and its lastActivityTime
 * so the two values cannot drift out of sync. See `getAutoLoadConversationKey` for site scoping.
 */
export const LS_AUTO_LOAD_CONVERSATION_KEY = 'aiMateChatLoadPreference';

/**
 * Returns a site-scoped localStorage key for storing the auto-load conversation state
 * (conversationId + lastActivityTime, JSON-encoded). This ensures auto-select on chat
 * mount is scoped to the correct site when the chat is remounted with a different siteId.
 */
export const getAutoLoadConversationKey = (siteId: string): string =>
	siteId ? `${LS_AUTO_LOAD_CONVERSATION_KEY}_${siteId}` : LS_AUTO_LOAD_CONVERSATION_KEY;
export const AI_MATE_EXPERIENCE_ID = 'ai-mate';
export const DIRECTORY_URL = '/gateway/api/directory/graphql';
/** 30 minutes in milliseconds */
export const TIMEOUT_BEFORE_NEW_CHAT: number = 30 * SECONDS_IN_MIN * MS_IN_SECOND;
/** 5 minutes in milliseconds */
export const SHORTER_TIMEOUT_BEFORE_NEW_CHAT: number = 5 * SECONDS_IN_MIN * MS_IN_SECOND;
/** Stable named-id sentinel for the default Rovo Chat agent. */
export const DEFAULT_AGENT_NAMED_ID = 'ai_mate_agent';

/**
 * Runtime UUID for the default Rovo Chat (General Agent).
 * Prefer is_default: true from the backend wherever available; this is a fallback sentinel for
 * cases where only an agent id is available.
 *
 * Note: this same UUID also serves as the Solution Architect Agent runtime identity used to gate
 * minion verification analytics (see minion-verification-analytics). The two identities share this
 * UUID, so updating it affects both default-agent attribution and minion verification gating.
 */
export const DEFAULT_AGENT_UUID = 'f3a534d9-1498-e0d4-8b50-e058a8fb8156';
/**
 * If you are updating this, please also update the pollinator repo so it will not fail during transition period
 * https://bitbucket.org/atlassian/pm2-ai-mate
 */
/**
 * Height of the greeting header in mini modal (in pixels).
 * Used to adjust minimum height calculation so users can resize below
 * the initial height when greeting is visible.
 */
export const MINI_MODAL_GREETING_HEIGHT = 140;

/**
 * Expanded max height cap for the mini modal (Rovo chat) when not limited by the viewport.
 */
export const MINI_MODAL_EXPANDED_HEIGHT = '600px';

export const POLLINATOR_TEST_IDS = {
	NEW_CHAT_BUTTON: 'rovo-chat-new-chat-button',
	THUMBS_UP_BUTTON: 'rovo-chat-thumbs-up-button',
};

/**
 * Default initial width (in pixels) of the right panel in the conversation
 * assistant split layout. Used when no persisted width is available.
 */
export const SPLIT_LAYOUT_INITIAL_WIDTH = 380;

/**
 * Minimum width (in pixels) for the main (left) panel of the conversation
 * assistant split layout. The right panel cannot be resized to leave the
 * left panel narrower than this.
 */
export const SPLIT_LAYOUT_MIN_WIDTH = 480;

/**
 * Minimum width (in pixels) for the right (adjacent) panel of the conversation
 * assistant split layout. The right panel cannot be resized below this.
 */
export const SPLIT_LAYOUT_ADJACENT_MIN_WIDTH = 280;
